import StepperElement from '../stepper/StepperElement';
import RightSidePlacementDriveDetails from './RightSidePlacementDriveDetails';
import ApplicationFormDetails from './ApplicationFormDetails/ApplicationFormDetails';
import {
	DayTimeSlot,
	DriveInviteeApplicationStatus,
	DriveInviteeDocument,
	FormStep,
	HighestQualification,
	JobseekerFormData,
	JobseekerFormJobType,
	PostgraduateDegreeTypes,
	SlotTime,
	UndergraduateDegreeTypes,
	UserAvailabilityInput,
	UserExperienceLevelType,
	WeekDay,
} from 'types';
import { useUserContext } from 'src/context/UserProvider';
import dayjs, { Dayjs } from 'dayjs';
import {
	useCreateDriveRegistrationApplicationMutation,
	useGetAllDrivesQuery,
	useGetDriveRegistrationApplication,
} from '../../hooks/drives.hooks';
import { useToast } from 'src/context/ToastProvider';
import { get } from 'lodash';
import React, { useEffect, useState } from 'react';
import EligibilityTestComponent from './EligibilityTestComponent/EligibilityTestComponent';
import { SlotsData } from 'types';
import ConfirmationPage from './EligibilityTestComponent/ConfirmationPage';
import CounsellingBooking from './CounsellingComponent/CounsellingBooking';
import PlacementPreparationPage from './PlacementPreparationComponent/PlacementPreparationPage';
import WaitListedScreen from './WaitlistedScreen';
import ResultsPendingScreen from './ResultsPendingScreen';
import ResultDateAnnouncingScreen from './ResultDateAnnouncingScreen';
import { handleS3Upload } from 'src/utils/imageUpload';
import { getPolicyForS3NonVideo } from 'src/apis/user';
import NotEligible from './NotEligible';

const ApplicationForm = () => {
	const toast = useToast();
	const {
		currentStep,
		setCurrentStep,
		applicationFormStep,
		setApplicationFormStep,
		applicationFormData,
		setApplicationFormData,
		updateFormData,
		goToNextStep,
		goToPreviousStep,
		goToNextFormStep,
		goToPreviousFormStep,
	} = useUserContext();

	const [allSlots, setAllSlots] = useState<DayTimeSlot[]>([]);
	const [allCousellingSlots, setAllCousellingSlots] = useState<DayTimeSlot[]>([]);
	const [eligibleStatus, setEligibleStatus] = useState(true);

	const getFormLabel = {
		[HighestQualification.TWELVE_PASS_OR_DIPLOMA]: 'Year you passed 12th Grade',
		[HighestQualification.UNDERGRADUATE]: 'Year of Graduation (Bachelors)',
		[HighestQualification.GRADUATE]: 'Year of Graduation (Masters)',
	};
	const formStepOrder: FormStep[] = [
		'educationQualification',
		'passingYear12Th',
		'educationDegree',
		'experience',
		'jobType',
		'skillsAndInterests',
		'driveLocation',
		'resume',
	];

	const sortApplicationDetails = (details: any[]) => {
		return details.sort((a, b) => formStepOrder.indexOf(a.key) - formStepOrder.indexOf(b.key));
	};

	const camelCaseToTitleCase = (str: string) => {
		return str.replace(/([A-Z])/g, ' $1').replace(/^./, function (str) {
			return str.toUpperCase();
		});
	};
	const educationOptions = [
		...new Set([Object.values(UndergraduateDegreeTypes), Object.values(PostgraduateDegreeTypes)].flat()),
	];
	const techJobOptions = [
		{
			label: 'Frontend Developer',
			value: 'Frontend_Developer',
			subValue: 'Creates website UI and ensures a great user experience.',
		},
		{
			label: 'Backend Developer',
			value: 'Backend_Developer',
			subValue: 'Manages server-side logic, databases, and application functionality.',
		},
		{
			label: 'Full Stack Developer',
			value: 'Full_Stack_Developer',
			subValue: 'Builds and maintains both front-end and back-end of web applications.',
		},
		{
			label: 'Quality Analyst',
			value: 'Quality_Analyst',
			subValue: 'Tests software to ensure quality, reliability, and bug-free performance.',
		},
	];

	const nonTechJobOptions = [
		{
			label: 'Sales',
			value: 'Sales',
			subValue: 'Handles client relationships and drives revenue.',
		},
		{
			label: 'Digital Marketing',
			value: 'Digital_Marketing',
			subValue: 'Promotes products/services through digital channels.',
		},
		{
			label: 'Customer Support',
			value: 'Customer_Support',
			subValue: 'Provides assistance and support to customers.',
		},
	];

	const [applicationDetails, setApplicationDetails] = useState([
		{
			key: 'educationQualification',
			title: 'Select your highest qualification',
			options: [
				{
					label: '12th Pass/Diploma',
					value: HighestQualification.TWELVE_PASS_OR_DIPLOMA,
				},
				{
					label: 'Undergraduate (Bachelors)',
					value: HighestQualification.UNDERGRADUATE,
				},
				{
					label: 'Graduate (Masters)',
					value: HighestQualification.GRADUATE,
				},
			],
		},
		{
			key: 'passingYear12Th',
			title:
				getFormLabel[
					(applicationFormData?.educationQualification as HighestQualification) ||
						HighestQualification.TWELVE_PASS_OR_DIPLOMA
				],
			isDate: true,
			dateFormat: 'YYYY',
		},
		{
			key: 'educationDegree',
			title: 'Select your Education degree',
			options: educationOptions?.map(degree => ({
				label: degree,
				value: degree,
			})),
			isMultiSelect: true,
		},
		{
			key: 'experience',
			title: 'Select your experience level',
			options: Object.values(UserExperienceLevelType).map(experience => ({
				label: camelCaseToTitleCase(experience),
				value: experience,
			})),
			isCompleted: false,
		},
		{
			key: 'jobType',
			title: 'What type of job are you looking for?',
			options: [
				{
					label: 'Tech jobs',
					value: 'Tech_Jobs',
				},
				{
					label: 'Non-tech jobs',
					value: 'Non_Tech_Jobs',
				},
			],
		},
		{
			key: 'skillsAndInterests',
			title: 'Which role best matches your skills and interests?',
			previousDependent: true,
			previousSelectedOption: {
				Tech_Jobs: [
					{
						label: 'Software Developer',
						value: 'Software_Developer',
						subValue: 'Develops, tests, and maintains software applications.',
					},
				],
				Non_Tech_Jobs: [
					{
						label: 'Business Development Associate (BDA)',
						value: 'Business_Development_Associate_(BDA)',
						subValue: 'Finds new business opportunities to help the company grow.',
					},
					{
						label: 'Sales Executive',
						value: 'Sales_Executive',
						subValue: 'Sells products or services to customers and meets sales targets.',
					},
					{
						label: 'Field Sales Representative',
						value: 'Field_Sales_Representative',
						subValue: 'Visits customers to sell products and build relationships.',
					},
					{
						label: 'Customer Relationship Executive (CRE)',
						value: 'Customer_Relationship_Executive_(CRE)',
						subValue: 'Helps customers and keeps them happy with the company.',
					},
					{
						label: 'Account Executive (Junior Role)',
						value: 'Account_Executive_(Junior_Role)',
						subValue: 'Manages client accounts and supports sales.',
					},
				],
			},
		},
		{
			key: 'driveLocation',
			title: 'Preferred placement drive location?',
			options: [
				{
					label: 'Bangalore (17-18th Jan)',
					value: 'Bangalore',
				},
				{
					label: 'Delhi (17-18th Jan)',
					value: 'Delhi',
				},
				{
					label: 'Chennai (17-18th Jan)',
					value: 'Chennai',
				},
				{
					label: 'Pune (17-18th Jan)',
					value: 'Pune',
				},
				{
					label: 'Other',
					value: 'Other',
				},
			],
		},
		{
			key: 'resume',
			title: 'Upload your resume (optional)',
			isFileUpload: true,
		},
	]);
	useEffect(() => {
		setApplicationDetails((prevDetails: any) => {
			return prevDetails.map((detail: any) => {
				if (detail.key === 'skillsAndInterests') {
					return {
						...detail,
						options: applicationFormData?.jobType === 'Non_Tech_Jobs' ? nonTechJobOptions : techJobOptions,
					};
				}
				if (detail.key === 'passingYear12Th') {
					return {
						...detail,
						title:
							getFormLabel[applicationFormData?.educationQualification as HighestQualification] ||
							getFormLabel[HighestQualification.TWELVE_PASS_OR_DIPLOMA],
					};
				}
				return detail;
			});
		});
	}, [applicationFormData?.jobType, applicationFormData?.educationQualification]);

	const {
		data: driveRegistrationApplication,
		isLoading: driveRegistrationApplicationIsLoading,
		refetch: driveRegistrationApplicationRefetch,
	} = useGetDriveRegistrationApplication({
		onComplete: data => {
			setCurrentStep(data?.currentStep + 1);
		},
	});

	const { data: drivesData, refetch: drivesRefetch } = useGetAllDrivesQuery({
		onComplete: data => {
			setApplicationDetails(prev =>
				sortApplicationDetails([
					...prev?.filter(drive => drive.key !== 'driveLocation'),
					{
						key: 'driveLocation',
						title: 'Preferred placement drive location?',
						options: [
							...data?.drives?.map(drive => ({
								label: `${drive.city} (${dayjs(drive.driveDate).format(`${drive.driveEndDate ? ' Do' : 'Do MMM'}`)}${
									drive.driveEndDate ? '-' : ''
								}${drive.driveEndDate ? dayjs(drive.driveEndDate).format('Do MMM') : ''})`,
								value: drive._id,
							})),
							{
								label: 'Other',
								value: 'Other',
							},
						],
					},
				]),
			);
		},
	});

	useEffect(() => {
		if (drivesData?.drives && applicationFormData?.jobType) {
			setApplicationDetails(prev =>
				sortApplicationDetails([
					...prev?.filter(drive => drive.key !== 'driveLocation'),
					{
						key: 'driveLocation',
						title: 'Preferred placement drive location?',
						options: [
							...drivesData?.drives
								?.filter(drive =>
									drive?.driveDetails?.driveTag?.length && applicationFormData?.jobType
										? drive?.driveDetails?.driveTag?.includes(applicationFormData?.jobType)
										: true,
								)
								?.map(drive => ({
									label: `${drive.city} (${dayjs(drive.driveDate).format(`${drive.driveEndDate ? 'Do' : 'Do MMM'}`)}${
										drive.driveEndDate ? '-' : ''
									}${drive.driveEndDate ? dayjs(drive.driveEndDate).format('Do MMM') : ''})`,
									value: drive._id,
								})),
							{
								label: 'Other',
								value: 'Other',
							},
						],
					},
				]),
			);
		}
	}, [applicationFormData, drivesData]);

	const { mutate: createDriveApplication, isLoading: isCreatingDriveApplication } =
		useCreateDriveRegistrationApplicationMutation({
			onSuccess: data => {
				setCurrentStep(data?.currentStep ? data?.currentStep + 1 : Number(2));
				driveRegistrationApplicationRefetch();
				toast({
					title: data?.message || 'Drive application created successfully',
					type: 'success',
				});
			},
			onError: error => {
				const errorMessage = get(error, ['response', 'data', 'message'], error.message);
				toast({
					title: errorMessage || 'Failed to create drive application',
					type: 'error',
				});
			},
		});

	const steps = [
		{
			id: 1,
			key: 'Application Form',
			value: '1',
			isActive: false,
			isCompleted: false,
		},
		{
			id: 2,
			key: 'Eligibility Test',
			value: '2',
			isActive: false,
			isCompleted: false,
		},
		{
			id: 3,
			key: 'Counselling',
			value: '3',
			isActive: false,
			isCompleted: false,
		},
		{
			id: 4,
			key: 'Placement preparation',
			value: '4',
			isActive: false,
			isCompleted: false,
		},
		{
			id: 5,
			key: 'Drive details',
			value: '5',
			isActive: false,
			isCompleted: false,
		},
	];

	const handleOptionSelect = ({
		key,
		optionValue,
		changePage = true,
	}: {
		key: string;
		optionValue: string | string[] | Dayjs | Date | File | undefined;
		changePage?: boolean;
	}) => {
		if (key === 'educationDegree' && (!optionValue || (Array.isArray(optionValue) && optionValue.length === 0))) {
			toast({
				title: 'Education degree is mandatory',
				type: 'error',
			});
			return;
		}
		if (key === 'driveLocation' && optionValue === 'Other') {
			setEligibleStatus(false);
			return;
		}
		updateFormData(key, optionValue);
		if (applicationFormStep < applicationDetails.length && changePage) {
			goToNextFormStep();
		}
	};

	const handleSubmitApplictionForm = async () => {
		let imageUrl = '';
		const file =
			applicationFormData?.resume && applicationFormData.resume instanceof File ? applicationFormData.resume : null;
		if (file) {
			const fileSizeMB = file ? file.size / (1024 * 1024) : 0; // Convert bytes to MB
			if (fileSizeMB > 5) {
				toast({
					type: 'error',
					title: 'File size exceeds the maximum limit of 5 MB',
					duration: 10000,
				});
				return;
			}

			// fileName, fileLocation, mime
			const policy = await getPolicyForS3NonVideo(file.name, 'interviewThumbnail', file.type, true);
			const uploadedUrl = await handleS3Upload(policy, file);
			if (uploadedUrl) imageUrl = uploadedUrl;
		}
		createDriveApplication({
			driveId: applicationFormData?.driveLocation || '',
			jobseekerFormData: {
				highestQualification: applicationFormData?.educationQualification || '',
				twelthGraduationYear: dayjs(applicationFormData?.passingYear12Th).year() || 0,
				educationDegree: Array.isArray(applicationFormData?.educationDegree)
					? applicationFormData?.educationDegree
					: [],
				experience: applicationFormData?.experience || '',
				jobType: applicationFormData?.jobType || JobseekerFormJobType.TECH_JOBS,
				jobRole: applicationFormData?.skillsAndInterests || '',
				resume: imageUrl,
			},
		});
	};

	const handleResetForm = () => {
		setApplicationFormData({
			educationQualification: null,
			passingYear12Th: null,
			educationDegree: null,
			experience: null,
			jobType: null,
			skillsAndInterests: null,
			driveLocation: null,
			resume: null,
		});
		setCurrentStep(1);
		setApplicationFormStep(1);
	};

	const processSlots = (userAvailabilities: UserAvailabilityInput[], setSlots: (slots: DayTimeSlot[]) => void) => {
		if (!userAvailabilities || !userAvailabilities.length) {
			console.log('from here');
			setSlots([]);
			return;
		}

		const currentTime = dayjs();

		const filteredAvailabilities = userAvailabilities.filter(avail => dayjs(avail.startTime).isAfter(currentTime));

		const dayWiseSlots = filteredAvailabilities
			.sort((avail1, avail2) => dayjs(avail1.startTime).valueOf() - dayjs(avail2.startTime).valueOf())
			.reduce((acc: Record<string, any>, userAvailability) => {
				const formattedDate = dayjs(userAvailability.startTime).format('DD MMM');
				if (!acc[formattedDate]) {
					acc[formattedDate] = {
						day: userAvailability.day,
						date: userAvailability.startTime,
						duration: userAvailability.duration,
						slotTimes: [{ time: userAvailability.startTime, count: 1 }],
						totalSlots: 1,
					};
				} else {
					const dayObject = acc[formattedDate];
					const slotTimeIndex = dayObject.slotTimes.findIndex(
						(time: { time: string | number | dayjs.Dayjs | Date | null | undefined }) =>
							dayjs(time.time).valueOf() === dayjs(userAvailability.startTime).valueOf(),
					);
					if (slotTimeIndex < 0) {
						acc[formattedDate] = {
							...dayObject,
							slotTimes: [...dayObject.slotTimes, { time: userAvailability.startTime, count: 1 }],
							totalSlots: dayObject.totalSlots + 1,
						};
					}
				}
				return acc;
			}, {});

		const availableSlots = Object.values(dayWiseSlots)
			.filter(ds => ds.slotTimes.length > 0)
			.map(daySlot => ({
				...daySlot,
				slotTimes: daySlot.slotTimes.sort(
					(
						slot1: { time: string | number | dayjs.Dayjs | Date | null | undefined },
						slot2: { time: string | number | dayjs.Dayjs | Date | null | undefined },
					) => dayjs(slot1.time).valueOf() - dayjs(slot2.time).valueOf(),
				),
			}));

		setSlots(availableSlots);
	};

	useEffect(() => {
		const testSlots = driveRegistrationApplication?.driveApplication?.drive?.driveDetails?.testSlots || [];
		const counsellingSlots =
			driveRegistrationApplication?.driveApplication?.drive?.driveDetails?.counsellingSlots || [];

		processSlots(testSlots, setAllSlots);
		processSlots(counsellingSlots, setAllCousellingSlots);
	}, [
		driveRegistrationApplication?.driveApplication?.drive?.driveDetails?.testSlots,
		driveRegistrationApplication?.driveApplication?.drive?.driveDetails?.counsellingSlots,
	]);

	const EligibleStatusHandler = () => {
		setEligibleStatus(true);
	};

	const resultDate = driveRegistrationApplication?.driveApplication?.drive?.driveDetails?.assessmentResultDate
		? dayjs(driveRegistrationApplication?.driveApplication?.drive?.driveDetails?.assessmentResultDate).format(
				'DD MMM, hh:mm a',
		  )
		: dayjs(driveRegistrationApplication?.driveApplication?.userApplicationDetails?.testSlot?.endTime)
				.add(3, 'days')
				.format('DD MMM, hh:mm a');

	return (
		<div className="pt-4 flex-1 flex flex-col max-w-[1120px] mx-auto w-full pb-4">
			{/* <div>
				<button onClick={goToPreviousStep} disabled={currentStep === 1}>
					Prev
				</button>
				<button onClick={goToNextStep} disabled={currentStep === steps.length}>
					Next
				</button>
				<button onClick={handleResetForm}>Reset Form</button>
			</div> */}
			<div className="border-b border-gray-200 pb-2 px-4 txl:px-0">
				<h2 className="text-lg font-[700] ">Masai Drive Placement</h2>
			</div>
			<div className="px-4 txl:px-0 mt-4 overflow-auto remove-scrollbar w-full">
				<StepperElement key={1} steps={steps} scrollable={false} currentStep={currentStep} />
			</div>
			<div className="flex w-full pt-6 justify-between flex-col md:flex-row gap-2 md:gap-8">
				{/* Left Side */}
				<div className="w-full flex-1">
					{currentStep === 1 && (
						<div className="px-4 txl:px-0 w-full">
							{!eligibleStatus ? (
								<NotEligible editHandler={EligibleStatusHandler} />
							) : (
								<ApplicationFormDetails
									currentFormStep={applicationFormStep}
									applicationFormData={applicationFormData}
									applicationDetails={applicationDetails}
									handleOptionSelect={handleOptionSelect}
									handlePreviousStep={goToPreviousFormStep}
									handleSubmit={handleSubmitApplictionForm}
								/>
							)}
						</div>
					)}
					{currentStep === 2 &&
						(driveRegistrationApplication?.driveApplication?.userApplicationDetails?.status ===
						DriveInviteeApplicationStatus.ASSESSMENT_FAILED ? (
							<WaitListedScreen studentName={driveRegistrationApplication?.driveApplication?.user?.name} />
						) : driveRegistrationApplication?.driveApplication?.userApplicationDetails?.status ===
						  DriveInviteeApplicationStatus.ASSESSMENT_PENDING_RESULTS ? (
							<>
								{dayjs(driveRegistrationApplication?.driveApplication?.userApplicationDetails?.testSlot?.endTime)
									.add(3, 'days')
									.isAfter(dayjs()) ? (
									<ResultDateAnnouncingScreen date={resultDate} />
								) : (
									<ResultsPendingScreen />
								)}
								{/* {dayjs(driveRegistrationApplication?.driveApplication?.drive?.driveDetails?.assessmentResultDate)
									.add(3, 'days')
									.isAfter(dayjs()) ? (
									<ResultDateAnnouncingScreen date={driveRegistrationApplication?.driveApplication?.drive?.driveDetails?.assessmentResultDate} />
								) : (
									<ResultsPendingScreen />
								)} */}
							</>
						) : driveRegistrationApplication?.driveApplication?.userApplicationDetails?.status ===
						  DriveInviteeApplicationStatus.ASSESSMENT ? (
							<div className="px-2 txl:px-0 w-full">
								<ConfirmationPage driveInvitee={driveRegistrationApplication?.driveApplication} />
							</div>
						) : (
							<div className="px-4 txl:px-0 w-full">
								<EligibilityTestComponent
									slots={allSlots}
									isLoading={driveRegistrationApplicationIsLoading}
									driveApplicationId={driveRegistrationApplication?.driveApplication?._id}
									note="Note: The fee will be fully refunded if you are not eligible."
									headerTitle={`Pay & schedule your ${
										allSlots?.[0]?.duration ? allSlots?.[0]?.duration : '0'
									} min eligibility test`}
									userApplicationDetails={driveRegistrationApplication?.driveApplication?.userApplicationDetails}
									description="The eligibility test fee ensures commitment and a fair process for all applicants."
									currency="₹"
									qualifierFee={
										driveRegistrationApplication?.driveApplication?.drive?.driveDetails?.meta?.firstPaymentAmount
									}
								/>
							</div>
						))}
					{currentStep === 3 && (
						<div className="px-4 txl:px-0 w-full">
							<CounsellingBooking
								slots={allCousellingSlots}
								driveRegistrationApplication={driveRegistrationApplication?.driveApplication as DriveInviteeDocument}
							/>
						</div>
					)}
					{currentStep === 4 && (
						<div className="px-4 txl:px-0 w-full">
							<PlacementPreparationPage
								driveApplicationId={driveRegistrationApplication?.driveApplication?._id || ''}
								driveRegistrationApplication={driveRegistrationApplication?.driveApplication?.drive}
								driveRegistrationFormData={
									driveRegistrationApplication?.driveApplication?.jobseekerFormData || ({} as JobseekerFormData)
								}
							/>
						</div>
					)}
					{currentStep === 5 && (
						<div className="flex flex-col mx-auto rounded-[8px] justify-center items-center h-full">
							<div className="max-w-md w-full flex flex-col items-center text-center pb-20">
								<div className="mx-auto">
									<img src="/assets/assessmentcompleted/Pending.svg" className="w-full h-full" />
								</div>
								<h1 className="text-[20px] font-inter font-[700] text-[#0A0103] leading-[28px] mt-4">All Set!</h1>
								<div className="space-y-2 text-[#544D4F] text-[16px] leading-[24px] font-[600] mt-3">
									You have successfully completed <br /> your counselling. <br /> Your Placement preparation will be
									available soon.
								</div>
							</div>
						</div>
					)}
					{currentStep > 5 && <div>last</div>}
				</div>

				{/* Right Side */}
				<div className="flex-1 w-full max-w-[410px] overflow-visible pr-4 xl:pr-0">
					<RightSidePlacementDriveDetails
						driveRegistrationApplication={driveRegistrationApplication?.driveApplication?.drive}
					/>
				</div>
			</div>
		</div>
	);
};

export default ApplicationForm;
